import * as React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { getFieldNotes, submitFieldNoteSync, toSyncDelayMsInt } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import { useStorageCapability } from "@/hooks/useStorageCapability";
import { useStorageEstimate } from "@/hooks/useStorageEstimate";
import { MY_FIELD_NOTES_FETCH_LIMIT } from "@/components/MyFieldNotesList";
import { isFieldNoteFormValid } from "@/lib/fieldNotes";
import { enqueueFieldNote } from "@/lib/outbox";
import { requestSync } from "@/lib/syncScheduler";
import {
  checkBackendReachable,
  errorMessage,
  isStorageFailure,
  localSaveFailureMessage,
  unreachable,
  type ReachabilityResult,
} from "@/pages/dashboard/offlineSave";
import { useDashboardRoles } from "@/pages/dashboard/useDashboardRoles";

function _getNow() { return performance.now(); }
function _getUuid() { return (crypto as any).randomUUID ? (crypto as any).randomUUID() : ("u" + performance.now()); }
function _getIso() { return new Date().toISOString(); }

export function useFieldNotesViewModel() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const online = useOnlineStatus();
  const { degraded: storageDegraded } = useStorageCapability();
  const { refresh: refreshStorage } = useStorageEstimate();
  const { isAdmin } = useDashboardRoles();

  // Standalone field notes state
  const [fieldNoteForm, setFieldNoteForm] = React.useState({
    additional_note: "",
    correction_type: "",
    reason_for_correction: "",
  });
  const [fieldNoteSubmitError, setFieldNoteSubmitError] = React.useState<string | null>(null);
  // null = not saved; {direct?:true} = saved (direct fallback means no local outbox entry)
  const [fieldNoteSaved, setFieldNoteSaved] = React.useState<{ direct?: boolean } | null>(null);
  const { data: fieldNotesData } = useQuery({
    queryKey: ["field-notes"],
    queryFn: () => getFieldNotes({ limit: MY_FIELD_NOTES_FETCH_LIMIT, offset: 0 }),
    enabled: !!user,
  });


  async function handleFieldNoteSubmit() {
    if (!user) return;
    if (!isFieldNoteFormValid(fieldNoteForm)) {
      setFieldNoteSubmitError("Type of Correction and Reason for Correction are required.");
      setFieldNoteSaved(null);
      return;
    }
    const noteCreatedAt = _getNow();
    const clientUuid = _getUuid();
    const payload = {
      additional_note: fieldNoteForm.additional_note,
      correction_type: fieldNoteForm.correction_type || null,
      reason_for_correction: fieldNoteForm.reason_for_correction || null,
      submitted_at: _getIso(),
    };
    setFieldNoteSubmitError(null);
    setFieldNoteSaved(null);

    let usedDirect = false;
    const submitNoteDirect = async () => {
      await submitFieldNoteSync({
        ...payload,
        client_uuid: clientUuid,
        sync_delay_ms: toSyncDelayMsInt(_getNow() - noteCreatedAt),
      });
      usedDirect = true;
    };

    if (storageDegraded) {
      if (!online) {
        setFieldNoteSubmitError(
          "Offline field notes are unavailable in private browsing. Use normal Safari and go online.",
        );
        return;
      }
      try {
        await submitNoteDirect();
      } catch (directErr) {
        const dmsg = directErr instanceof Error ? directErr.message : "Network error";
        setFieldNoteSubmitError(`Could not submit the field note: ${dmsg}`);
        return;
      }
    } else {
      try {
        await enqueueFieldNote({ userId: user.id, payload });
      } catch (error) {
        const msg = errorMessage(error);
        const looksLikeStorage = isStorageFailure(error);

        const reachability: ReachabilityResult = looksLikeStorage
          ? await checkBackendReachable().catch(() => unreachable("the server reachability check failed"))
          : unreachable("the error was not a storage failure");

        if (looksLikeStorage && reachability.reachable) {
          try {
            await submitNoteDirect();
          } catch (directErr) {
            const dmsg = directErr instanceof Error ? directErr.message : "Network error";
            setFieldNoteSubmitError(
              `Could not save the field note on this device (${msg}). Direct server submit also failed: ${dmsg}.`
            );
            return;
          }
        } else if (looksLikeStorage) {
          setFieldNoteSubmitError(localSaveFailureMessage("field note", msg, reachability));
          return;
        } else {
          const full = msg.toLowerCase().includes("connection") || msg.toLowerCase().includes("lost")
            ? `Could not save the field note on this device: ${msg}. Refresh the page to try again.`
            : `Could not save the field note on this device: ${msg} Free up storage and try again.`;
          setFieldNoteSubmitError(full);
          return;
        }
      }
    }

    setFieldNoteForm({
      additional_note: "",
      correction_type: "",
      reason_for_correction: "",
    });

    setFieldNoteSaved(usedDirect ? { direct: true } : {});
    refreshStorage();

    if (usedDirect) {
      void queryClient.invalidateQueries({ queryKey: ["field-notes"] });
      void queryClient.invalidateQueries({ queryKey: ["field-notes-count"] });
      void queryClient.invalidateQueries({ queryKey: ["field-notes-review"] });
      void queryClient.invalidateQueries({ queryKey: ["punch-notes-review"] });
      void queryClient.invalidateQueries({ queryKey: ["punch-notes-review-count"] });
    } else {
      requestSync({ force: true });
    }
  }

  return {
    user,
    isAdmin,
    fieldNoteForm,
    setFieldNoteForm,
    fieldNoteSubmitError,
    fieldNoteSaved,
    fieldNotesData,
    handleFieldNoteSubmit,
    nowMs: _getNow,
  };
}
